/*
    Getting styled HTML past the WAF (MPN-1706).

    A WAF rule in front of every environment rejects a request body containing anything that
    looks like CSS — a `style=` attribute, a `<style>` block, a stylesheet `<link>` — with a
    403 before the request reaches the API. Measured on dev and prod.

    Every real postcard is styled HTML, so without this the browser cannot save a mail piece
    or run a test send at all, while the same artwork published by a background job goes out
    fine because it never crosses the WAF. That gap is why this looked healthy everywhere
    except in a browser.

    This is a workaround, not a fix. The fix is a scoped WAF exclusion for the two endpoints
    that legitimately carry HTML, and this should be deleted when that lands.
*/

// btoa() only handles Latin-1, so a postcard with an accent in it throws. Client copy is not
// ASCII — "Réussissez votre inscription" has to print — so the string is encoded to UTF-8
// bytes first and those bytes are what get base64'd.
export function encodeArtwork(html: string): string {
    if (!html) return "";
    const bytes = new TextEncoder().encode(html);
    let binary = "";
    // Chunked rather than String.fromCharCode(...bytes): a 10,000 character postcard spreads
    // into 10,000 arguments and blows the call stack.
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
}

// What each channel's server will accept, so an editor can say so before a save round trips
// into a 400. Deliberately the same two numbers as utils/html_transport.py: they are different
// because the constraints are different, and sharing mail's cap with email was a real bug
// that refused an ordinary 16KB template with a message about a printer.
export const MAX_MAIL_HTML = 10_000;
export const MAX_EMAIL_HTML = 256_000;
// Where Gmail stops showing a message and offers "View entire message" instead, taking the
// unsubscribe footer with it. Not a limit, a warning: plenty of clients do not clip, and
// refusing a send over it would be inventing a rule only Google has.
export const GMAIL_CLIP_BYTES = 102_000;

// The encoded length, which is what the request actually carries. Base64 costs a third on top
// and an accented character costs two bytes before that, so a template measured in characters
// can pass here and still be refused by the server.
export function encodedSize(html: string): number {
    return new TextEncoder().encode(html || "").length;
}
